/* eslint-disable camelcase, max-len, no-param-reassign */
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import axios from 'axios';
import AgentRate from './agentrate';
import AgentCostBreakDown from './AgentCostBreakdown';
import AddPaymentContainer from '../../../payments/addpayment/addPaymentContainer';
import ExtraRates from './ExtraRates';
import ConsultantSplits from './ConsultantSplits';
import PaymentDropdown from '../../paymentmodal/PaymentDropdown';
import { updateField, deleteField } from '../../../../actions/BookingRequest';
import { TRootState } from '../../../../reducers';
import { jobModes, matchingModeCodes, matchingModeLabels } from '../../../../consts';
import { findKeyByValue } from '../../../../utils';
import { checkFieldsValidation, checkFieldsAreNotEmpty } from '../../utils';
import { loadAgentsforAgency } from '../../../../actions/Agents';
import { TConsultantSplit, TJobRequestExtraRate } from '../../../operations/shifts/AgentShiftForm';
import { ROOT_URL_V2 } from '../../../../config';
import ConfirmShift from './ConfirmShift';
import { TBooking, TDbsCheck, TJobItem, TMatchingMode, TServiceKey } from '../../../../types';
import QualifyingQuestions, { TQualifyingQuestion } from './QualifyingQuestions';


type TAgentDelegateCard = {
    className?: string;
    handleFormSubmit: (formData: any) => void;
    isLoading: boolean;
    mode: string;
    natureRef: Element | any;
    qualificationsRef: Element | any;
    risksRef: Element | any;
    facilitiesRef: Element | any;
    payableRef: Element | any;
    titleRef: Element | any;
    natureLabelRef: Element | any;
    qualificationsLabelRef: Element | any;
    risksLabelRef: Element | any;
    facilitiesLabelRef: Element | any;
    payableLabelRef: Element | any;
    titleLabelRef: Element | any;
    initialExtraRates: TJobRequestExtraRate[];
    copyBookings: boolean;
    isMultiService: boolean;
    selectedServices: TServiceKey[];
    initialService?: TServiceKey;
    isSeries?: boolean;
};

export type TAdditionalService = TJobItem

export type TShiftData = {
    client: number;
    service: TServiceKey;
    vehicle_type?: number | string;
    role?: string;
    number_of_freelancers: number;
    address1: string;
    address2?: string;
    postcode: string;
    city: string;
    freelancer_pay_per_hour: number | '';
    client_pay_per_hour: number | '';
    matching_mode: TMatchingMode;
    dbs_check: TDbsCheck;
    isMultiple: boolean;
    date?: string;
    dates?: string[];
    rates?: TJobRequestExtraRate[];
    bookings: TBooking[];
    bookings_to_duplicate: number[];
    consultant_splits: TConsultantSplit[];
    additional_services: TAdditionalService[];
    qualifying_questions?: TQualifyingQuestion[];
}

type TBasicRates = {
    freelancer_pay_per_hour: number | '';
    client_pay_per_hour: number | '';
}

export function AgentDelegateCard({
    className,
    handleFormSubmit,
    isLoading,
    mode,
    natureRef,
    qualificationsRef,
    risksRef,
    facilitiesRef,
    payableRef,
    titleRef,
    natureLabelRef,
    qualificationsLabelRef,
    risksLabelRef,
    facilitiesLabelRef,
    payableLabelRef,
    titleLabelRef,
    initialExtraRates,
    copyBookings,
    isMultiService,
    selectedServices,
    initialService,
}: TAgentDelegateCard) {
    const [addPayment, setAddPayment] = useState('');
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [submitClicked, setSubmitClicked] = useState(false);
    const [basicRates, setBasicRates] = useState<TBasicRates>({
        freelancer_pay_per_hour: '',
        client_pay_per_hour: ''
    });
    const [extraRatesFromAPI, setExtraRatesFromAPI] = useState([]);
    const [extraRates, setExtraRates] = useState([]);
    const [consultantSplits, setConsultantSplits] = useState([]);
    const [isModalConfirmShiftOpen, setIsModalConfirmShiftOpen] = useState(false);
    const [shiftData, setShiftData] = useState<TShiftData>();
    const [qualifyingQuestions, setQualifyingQuestions] = useState([]);

    const {
        basicInfo,
        bookingRequest,
        clientDataError,
        roleData,
        user,
        matchingMode,
        defaultConsultant,
        dbsCheck,
        agents,
        isFetching,
        agentsLoadingError,
        existingQualifyingQuestions,
    } = useSelector((state: TRootState) => ({
        basicInfo: state.clientData.basicInfo,
        bookingRequest: state.requestData.bookingRequest,
        clientDataError: state.clientData.error,
        roleData: state.requestData.roleData,
        user: state.user,
        matchingMode: state.requestData.bookingRequest.matching_mode,
        defaultConsultant: state.requestData.bookingRequest.consultant_splits, // Selected consultant per new job request
        dbsCheck: state.requestData.bookingRequest.dbs_check,
        agents: state.agents.content,
        isFetching: state.agents.fetching,
        agentsLoadingError: state.agents.error,
        existingQualifyingQuestions: state.requestData.bookingRequest.qualifying_questions,
    }));

    const dispatch = useDispatch();

    const fields = [
        payableRef,
        facilitiesRef,
        risksRef,
        qualificationsRef,
        natureRef,
        titleRef,
    ];

    const labels = [
        payableLabelRef,
        facilitiesLabelRef,
        risksLabelRef,
        qualificationsLabelRef,
        natureLabelRef,
        titleLabelRef,
    ];

    useEffect(() => {
        if (!agents?.length && !isFetching && !agentsLoadingError) {
            dispatch(loadAgentsforAgency(user.agency.id));
        }
    }, [dispatch, agents, isFetching, agentsLoadingError, user.agency.id]);

    useEffect(() => {
        const getExtraRatesFromAPI = async() => {
            try {
                const response = await axios({
                    method: 'GET',
                    url: `${ROOT_URL_V2}rates/`,
                });

                setExtraRatesFromAPI(response.data?.content);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.log(error);
            }
        };
        getExtraRatesFromAPI();
    }, []);

    useEffect(() => {
        const _extraRates: TJobRequestExtraRate[] = extraRatesFromAPI.map(rateType => ({
            extra_rate: rateType.id,
        }));
        if (mode === jobModes.EDIT || mode === jobModes.DUPLICATE) {
            initialExtraRates.forEach(rate => {
                const index = _extraRates.findIndex(row => row.extra_rate === rate.extra_rate);
                _extraRates[index] = {
                    ..._extraRates[index],
                    freelancer_pay_per_hour: rate.freelancer_pay_per_hour,
                    client_pay_per_hour: rate.client_pay_per_hour,
                    description: rate.description,
                };
            });
        }
        setExtraRates(_extraRates);
    }, [extraRatesFromAPI]);

    useEffect(() => {
        setConsultantSplits(defaultConsultant);
    }, [defaultConsultant, mode, shiftData]);

    useEffect(() => {
        if (mode === jobModes.EDIT || mode === jobModes.DUPLICATE) {
            setQualifyingQuestions(existingQualifyingQuestions || []);
        }
    }, [existingQualifyingQuestions, mode, shiftData]);

    const handleOpenModalConfirmShift = () => {
        setIsModalConfirmShiftOpen(true);
    };

    const handleCloseModalConfirmShift = () => {
        setIsModalConfirmShiftOpen(false);
    };

    const handleMultiServices = (formData: TShiftData) => {
        let data = formData;
        const additionalServices: TAdditionalService[] = [];

        // if none of the selected services is highlighted, replace the main service
        if (!selectedServices.includes(formData.service)) {
            data = {
                ...data,
                service: selectedServices[0],
                ...roleData[selectedServices[0]],
            };
        }

        selectedServices.forEach(selectedService => {
            if (selectedService !== data.service) {
                additionalServices.push({
                    'service': selectedService,
                    ...roleData[selectedService],
                });
            }
        });
        if (additionalServices.length) {
            data.additional_services = additionalServices;
        }
        if (mode === jobModes.DUPLICATE && copyBookings) {
            const childBookings =
                bookingRequest.additional_services.flatMap((service: TAdditionalService) => service.bookings);
            data.bookings = [...data.bookings, ...childBookings];
        }
        return data;
    };

    const handleSubmit = (e: React.FormEvent<HTMLButtonElement>): void => {
        e.preventDefault();

        const service = initialService || bookingRequest.service;
        let formData: TShiftData = {
            ...bookingRequest,
            ...roleData[service],
            // 'basicRates.freelancer_pay_per_hour' and 'basicRates.client_pay_per_hour' values have to be added after
            // 'bookingRequest' in order to replace initial values
            ...basicRates,
        };

        setSubmitClicked(true);

        formData.service = service;
        if (selectedServices.length) {
            formData = handleMultiServices(formData);
        }
        if (formData.dates.length === 1) {
            formData.isMultiple = false;
            // eslint-disable-next-line prefer-destructuring
            formData.date = formData.dates[0];
            dispatch(updateField('isMultiple', formData.isMultiple));
        }

        if (mode !== jobModes.DUPLICATE || !copyBookings) {
            delete formData.bookings;
        } else {
            formData.bookings_to_duplicate = formData.bookings.map((booking: TBooking) => (
                booking.status === 'OK'
                    ? booking.id
                    : null
            )).filter(Boolean);
        }

        if (formData.isMultiple) delete formData.date;
        else delete formData.dates;

        if (formData.freelancer_pay_per_hour === null) {
            delete formData.freelancer_pay_per_hour;
        }
        if (formData.client_pay_per_hour === null) {
            delete formData.client_pay_per_hour;
        }
        if (formData.address2 === null) {
            delete formData.address2;
        }

        if (mode === jobModes.EDIT) {
            delete formData.client;
            delete formData.service;
        } else {
            formData.client = basicInfo.id;
        }

        checkFieldsValidation(fields, labels);

        if (checkFieldsAreNotEmpty(fields) && formData.consultant_splits?.length) {
            setShiftData(formData);
            handleOpenModalConfirmShift();
        }
    };

    const closeModal = useCallback(() => {
        setModalIsOpen(false);
    }, []);

    const onPaymentSelectChange = useCallback(
        (paymentMethod: any) => {
            if (paymentMethod && paymentMethod.id) {
                dispatch(updateField('payment_method', paymentMethod.id));
            } else if (
                paymentMethod &&
                paymentMethod.add_payment_option &&
                paymentMethod.value !== 'set_later'
            ) {
                setModalIsOpen(true);
                setAddPayment(paymentMethod.value);
            } else {
                dispatch(deleteField('payment_method'));
            }
        },
        [dispatch],
    );

    const isDisabled =
        !basicInfo?.id ||
        !bookingRequest.date ||
        !bookingRequest.service ||
        !bookingRequest.postcode ||
        clientDataError?.postcode ||
        (isMultiService && selectedServices.length < 2) ||
        isLoading;

    const matchingModeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
        dispatch(updateField('matching_mode', e.target.value));
    };

    const consultantHandler = (e: any) => {
        const value = Number(e.target.value);
        dispatch(updateField(
            'consultant_splits',
            value ? [{ agent: Number(e.target.value), split_value: 100 }] : [],
        ));
    };

    const dbsCheckHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
        dispatch(updateField('dbs_check', e.target.value));
    };

    return (
        <div className={classNames('card agent-delegate-card', className)}>
            <div className="card-content">
                <p className="card-title">Pricing</p>
                <p className="question">How much to pay for this job?</p>
                <AgentRate
                    bookingRequest={bookingRequest}
                    user={user}
                    basicRates={basicRates}
                    setBasicRates={setBasicRates}
                    mode={mode}
                />
                <ExtraRates
                    extraRatesFromAPI={extraRatesFromAPI}
                    extraRates={extraRates}
                    setExtraRates={setExtraRates}
                />
                {!user.agency.features.includes('self_managed') && basicInfo?.id && (
                    <div className="shift-form-field--matching small-margin-bottom">
                        <p className="title">Payment method used</p>
                        <PaymentDropdown
                            className="shift-form-field-input"
                            paymentMethod={bookingRequest.payment_method}
                            enableSetLater={true}
                            enableAddStripe={false}
                            onPaymentMethodSelect={onPaymentSelectChange}
                            mode={mode}
                        />
                        <AgentCostBreakDown
                            client_pay_per_hour={basicRates.client_pay_per_hour}
                            duration={bookingRequest.duration}
                            number_of_freelancers={bookingRequest.number_of_freelancers}
                        />
                    </div>
                )}
                <p className="card-title rounded">Additional options</p>
                <div className="option select">
                    <label htmlFor="matching_mode">Type of matching</label>
                    <select
                        className="option-select"
                        value={matchingMode}
                        onChange={matchingModeHandler}
                        name="matching_mode"
                        id="matching_mode"
                    >
                        {Object.values(matchingModeCodes).map(value =>
                            <option key={value} value={value}>
                                {/* @ts-ignore */}
                                {matchingModeLabels[findKeyByValue(matchingModeCodes, value)]}
                            </option>
                        )}
                    </select>
                </div>
                {(matchingMode !== matchingModeCodes.MANUAL || !!qualifyingQuestions?.length) &&
                    <QualifyingQuestions
                        initialQualifyingQuestions={qualifyingQuestions}
                        setQualifyingQuestions={setQualifyingQuestions}
                        mode={mode}
                    />
                }
                <div
                    className={classNames({
                        option: true,
                        select: true,
                        invalid: !defaultConsultant?.length && submitClicked,
                        'default-consultant-split': true,
                    })}
                    style={{ marginBottom: '1px' }}
                >
                    <label htmlFor="consultant">Consultant</label>
                    <select
                        className="option-select consultant"
                        value={consultantSplits?.length && consultantSplits[0].agent}
                        onChange={consultantHandler}
                        name="consultant"
                        id="consultant"
                        placeholder="Select Consultant..."
                        disabled={consultantSplits?.length > 1}
                    >
                        <option
                            data-testid="select-consultants-option"
                            label=" "
                        />
                        {agents.map(agent => (
                            // eslint-disable-next-line react/jsx-key
                            <option
                                data-testid="select-consultants-option"
                                key={`consultant-${agent.id}`}
                                value={agent.id}
                            >
                                {agent.full_name}
                            </option>
                        ))}
                    </select>
                </div>
                <ConsultantSplits
                    initialConsultantSplits={consultantSplits}
                    consultants={agents}
                    setConsultantSplits={setConsultantSplits}
                    mode={mode}
                    disabled={mode === jobModes.EDIT && !(bookingRequest.managed && bookingRequest.owned)}
                />
                <div className="option select">
                    <label htmlFor="dbs_check">DBS required</label>
                    <select
                        className="option-select"
                        value={dbsCheck}
                        onChange={dbsCheckHandler}
                        name="dbs_check"
                        id="dbs_check"
                    >
                        <option value="NO">None</option>
                        <option value="AR">DBS Application</option>
                        <option value="BA">DBS Basic</option>
                        <option value="EN">DBS Enhanced</option>
                    </select>
                </div>
                <ConfirmShift
                    handleClose={handleCloseModalConfirmShift}
                    handleFormSubmit={handleFormSubmit}
                    handleSubmit={handleSubmit}
                    isDisabled={isDisabled}
                    isOpen={isModalConfirmShiftOpen}
                    mode={mode}
                    shiftData={shiftData}
                />
                {addPayment && (
                    <AddPaymentContainer
                        mode={addPayment}
                        isOpen={modalIsOpen}
                        onRequestClose={closeModal}
                    />
                )}
            </div>
        </div>
    );
}
